
import React, { useState } from 'react';
import '../assets/Loan/EmiCalculator.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCalculator,
  faRupeeSign,
  faPercentage,
  faTable,
  faFileExcel,
  faFilePdf,
} from '@fortawesome/free-solid-svg-icons';
import { format, addDays, addMonths } from 'date-fns';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { toast } from 'react-toastify';

const LoanEmiCalculator = () => {
  const [inputs, setInputs] = useState({
    loanAmount: '',
    interestRate: '',
    interestRateType: 'Yearly',
    roiType: 'Flat',
    loanTerm: '',
    recoveryFrequency: 'Weekly',
    firstEmiDate: format(new Date(), 'yyyy-MM-dd'),
  });

  const [schedule, setSchedule] = useState([]);
  const [totals, setTotals] = useState({ emi: 0, principal: 0, interest: 0 });

  const handleChange = (e) => {
    const { id, value } = e.target;
    setInputs((prev) => ({ ...prev, [id]: value }));
  };

  const calculateEMI = () => {
    const principal = parseFloat(inputs.loanAmount);
    const roi = parseFloat(inputs.interestRate);
    const roiType = inputs.roiType.toLowerCase();
    const totalEMIs = parseInt(inputs.loanTerm);
    const recoveryFrequency = inputs.recoveryFrequency.toLowerCase();
    const firstEmiDate = new Date(inputs.firstEmiDate);
    const interestRateType = inputs.interestRateType.toLowerCase();

    if (!principal || !roi || !totalEMIs) {
      toast.warn('Please fill all required fields');
      return;
    }

    // Periods per year mapping
    const periodsPerYearMap = {
      daily: 365,
      weekly: 52,
      'bi-weekly': 26,
      'half-quarterly': 8,
      quarterly: 4,
      monthly: 12,
      yearly: 1,
    };
    const periodsPerYear = periodsPerYearMap[recoveryFrequency] || 12;

    // Add date based on frequency
    const addDate = (date, i) => {
      switch (recoveryFrequency) {
        case 'daily': return addDays(date, i);
        case 'weekly': return addDays(date, i * 7);
        case 'bi-weekly': return addDays(date, i * 14);
        case 'half-quarterly': return addDays(date, i * 45);
        case 'quarterly': return addMonths(date, i * 3);
        case 'monthly': return addMonths(date, i);
        case 'yearly': return addMonths(date, i * 12);
        default: return addMonths(date, i);
      }
    };

    // Periodic interest rate
    let periodRate = 0;
    if (interestRateType === 'yearly') periodRate = roi / 100 / periodsPerYear;
    else if (interestRateType === 'monthly') {
      switch (recoveryFrequency) {
        case 'daily': periodRate = roi / 100 / 30; break;
        case 'weekly': periodRate = roi / 100 / 4; break;
        case 'bi-weekly': periodRate = roi / 100 / 2; break;
        case 'half-quarterly': periodRate = roi / 100 / 2; break;
        case 'quarterly': periodRate = (roi / 100) * 3 / 12; break;
        case 'monthly': periodRate = roi / 100; break;
        case 'yearly': periodRate = (roi / 100) * 12; break;
        default: periodRate = roi / 100;
      }
    }

    let emi = 0;
    let totalInterestPaid = 0;
    let totalPrincipalPaid = 0;
    let scheduleData = [];
    let balance = principal;

    if (roiType === 'flat') {
      const totalInterest = principal * periodRate * totalEMIs;
      emi = (principal + totalInterest) / totalEMIs;
      const interestPerEMI = totalInterest / totalEMIs;
      const principalPerEMI = principal / totalEMIs;

      for (let i = 1; i <= totalEMIs; i++) {
        let principalComponent = principalPerEMI;
        let interestComponent = interestPerEMI;
        if (i === totalEMIs) principalComponent = balance;
        balance -= principalComponent;

        scheduleData.push({
          emiNo: i,
          emi: emi.toFixed(2),
          principal: principalComponent.toFixed(2),
          interest: interestComponent.toFixed(2),
          balance: balance > 0 ? balance.toFixed(2) : '0.00',
          dueDate: format(addDate(firstEmiDate, i - 1), 'yyyy-MM-dd'),
          interestType: 'Flat',
        });

        totalPrincipalPaid += principalComponent;
        totalInterestPaid += interestComponent;
      }
    } else {
      emi = (principal * periodRate * Math.pow(1 + periodRate, totalEMIs)) /
            (Math.pow(1 + periodRate, totalEMIs) - 1);

      for (let i = 1; i <= totalEMIs; i++) {
        let interestComponent = balance * periodRate;
        let principalComponent = emi - interestComponent;
        if (i === totalEMIs) principalComponent = balance;
        balance -= principalComponent;

        scheduleData.push({
          emiNo: i,
          emi: emi.toFixed(2),
          principal: principalComponent.toFixed(2),
          interest: interestComponent.toFixed(2),
          balance: balance > 0 ? balance.toFixed(2) : '0.00',
          dueDate: format(addDate(firstEmiDate, i - 1), 'yyyy-MM-dd'),
          interestType: 'Reducing',
        });

        totalPrincipalPaid += principalComponent;
        totalInterestPaid += interestComponent;
      }
    }















    
    setSchedule(scheduleData);
    setTotals({
      emi: (emi * totalEMIs).toFixed(2),
      principal: totalPrincipalPaid.toFixed(2),
      interest: totalInterestPaid.toFixed(2),
    });
  };

  const downloadPDF = () => {
    const doc = new jsPDF();
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(16);
    doc.text('Loan EMI Payment Schedule', 14, 15);

    const tableColumn = ['EMI NO.', 'AMOUNTS', 'PRINCIPAL', 'INTEREST', 'BALANCE', 'DUE DATE'];
    const tableRows = schedule.map((row) => [
      row.emiNo,
      `Rs. ${row.emi}`,
      `Rs. ${row.principal}`,
      `Rs. ${row.interest}`,
      `Rs. ${row.balance}`,
      row.dueDate,
    ]);
    tableRows.push([
      'Total',
      `Rs. ${totals.emi}`,
      `Rs. ${totals.principal}`,
      `Rs. ${totals.interest}`,
      '',
      '',
    ]);

    autoTable(doc, {
      head: [tableColumn],
      body: tableRows,
      startY: 25,
      styles: { fontSize: 10 },
      headStyles: { fillColor: [37, 99, 235] },
    });

    doc.save('EMI_Payment_Schedule.pdf');
  };

  return (
    <div className="BranchLoanEmiCalculator-container">
      {/* === FORM === */}
      <section className="BranchLoanEmiCalculator-formSection">
        <h1 className="BranchLoanEmiCalculator-heading">
          <FontAwesomeIcon icon={faCalculator} className="BranchLoanEmiCalculator-headingIcon" />
          <span>Loan EMI Calculator</span>
        </h1>

        <form className="BranchLoanEmiCalculator-form" noValidate>
          {/* Loan Amount */}
          <div className="BranchLoanEmiCalculator-inputGroup">
            <label htmlFor="loanAmount">Loan Amount</label>
            <div className="BranchLoanEmiCalculator-inputWrapper">
              <FontAwesomeIcon icon={faRupeeSign} className="BranchLoanEmiCalculator-icon" />
              <input
                type="number"
                id="loanAmount"
                value={inputs.loanAmount}
                onChange={handleChange}
                placeholder="Enter loan amount"
              />
            </div>
          </div>

          {/* Interest Rate */}
          <div className="BranchLoanEmiCalculator-inputGroup">
            <label htmlFor="interestRate">Interest Rate (%)</label>
            <div className="BranchLoanEmiCalculator-inputWrapper">
              <FontAwesomeIcon icon={faPercentage} className="BranchLoanEmiCalculator-icon" />
              <input
                type="number"
                step="0.01"
                id="interestRate"
                value={inputs.interestRate}
                onChange={handleChange}
                placeholder="Enter interest rate"
              />
            </div>
          </div>

          {/* Interest Type & ROI Type */}
          <div className="BranchLoanEmiCalculator-grid">
            <div className="BranchLoanEmiCalculator-inputGroup">
              <label htmlFor="interestRateType">Interest Rate Type</label>
              <select id="interestRateType" value={inputs.interestRateType} onChange={handleChange}>
                <option>Yearly</option>
                <option>Monthly</option>
              </select>
            </div>

            <div className="BranchLoanEmiCalculator-inputGroup-roi">
              <label htmlFor="roiType">ROI Type</label>
              <select id="roiType" value={inputs.roiType} onChange={handleChange}>
                <option>Flat</option>
                <option>Reducing</option>
              </select>
            </div>
          </div>

          {/* Loan Term */}
          <div className="BranchLoanEmiCalculator-tenure">
            <label htmlFor="loanTerm">Loan Term (Number of EMIs)</label>
            <input
              type="number"
              id="loanTerm"
              value={inputs.loanTerm}
              onChange={handleChange}
              placeholder="Enter number of EMIs"
            />
          </div>

          {/* EMI Frequency & First EMI Date */}
          <div className="BranchLoanEmiCalculator-grid">
            <div className="BranchLoanEmiCalculator-inputGroup-emifrequency">
              <label htmlFor="recoveryFrequency">EMI Frequency</label>
              <select id="recoveryFrequency" value={inputs.recoveryFrequency} onChange={handleChange}>
                <option>Daily</option>
                <option>Weekly</option>
                <option>Bi-Weekly</option>
                <option>Half-Quarterly</option>
                <option>Quarterly</option>
                <option>Monthly</option>
                <option>Yearly</option>
              </select>
            </div>

            <div className="BranchLoanEmiCalculator-inputGroup-date">
              <label htmlFor="firstEmiDate">First EMI Date</label>
              <input
                type="date"
                id="firstEmiDate"
                value={inputs.firstEmiDate}
                onChange={handleChange}
              />
            </div>
          </div>

          <button type="button" className="BranchLoanEmiCalculator-calcButton" onClick={calculateEMI}>
            <FontAwesomeIcon icon={faCalculator} /> Calculate EMI
          </button>
        </form>
      </section>

      {/* Payment Schedule Table */}
      <section className="BranchLoanEmiCalculator-resultSection">
        <h2 className="BranchLoanEmiCalculator-subHeading">
          <FontAwesomeIcon icon={faTable} className="BranchLoanEmiCalculator-subIcon" /> Payment Schedule
        </h2>

        <div style={{ display: 'flex', gap: '1rem', marginBottom: '1rem' }}>
          <button type="button" className="BranchLoanEmiCalculator-downloadButton">
            <FontAwesomeIcon icon={faFileExcel} /> Download Excel
          </button>

          <button
            type="button"
            className="BranchLoanEmiCalculator-downloadButton"
            style={{ background: 'linear-gradient(90deg, #dc2626, #b91c1c)' }}
            onClick={downloadPDF}
          >
            <FontAwesomeIcon icon={faFilePdf} /> Download PDF
          </button>
        </div>

        <div className="BranchLoanEmiCalculator-tableContainer">
          <table className="BranchLoanEmiCalculator-table">
            <thead>
              <tr>
                <th>EMI NO.</th>
                <th>AMOUNTS</th>
                <th>PRINCIPAL</th>
                <th>INTEREST</th>
                <th>BALANCE</th>
                <th>DUE DATE</th>
              </tr>
            </thead>

            <tbody>
              {schedule.map((row, index) => (
                <tr key={index}>
                  <td>{row.emiNo}</td>
                  <td>₹{row.emi}</td>
                  <td>₹{row.principal}</td>
                  <td>₹{row.interest}</td>
                  <td>₹{row.balance}</td>
                  <td>{row.dueDate}</td>
                </tr>
              ))}

              <tr>
                <td><strong>Total:</strong></td>
                <td>₹{totals.emi}</td>
                <td>₹{totals.principal}</td>
                <td>₹{totals.interest}</td>
                <td></td>
                <td></td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
};

export default LoanEmiCalculator;
